import { formatDate } from '@angular/common';
import { PdfToiChecklistItem, PdfToiEconomiaPorCategoriaRow, PdfToiIrregularidadeMarcarItem } from '../models/pdf-toi.model';
import { ToiDetalhe } from '../models/toi-detalhe.model';

export type PdfToiEconomiaCategoriaCampo =
  | 'residencial'
  | 'social'
  | 'comercial'
  | 'industrial'
  | 'publica';

/**
 * Máscara do nome do técnico no PDF: oculta os nomes anteriores preservando o
 * tamanho de cada um e exibe apenas o último
 * (ex.: "Maria da Silva" → "***** ** Silva").
 */
export function mascararNomePdf(valor: string | null | undefined): string {
  const partes = (valor ?? '')
    .replace(/[\t\n\r]/g, ' ')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!partes.length) {
    return '';
  }

  const ultimoNome = partes[partes.length - 1];
  const anteriores = partes.slice(0, -1).map((parte) => '*'.repeat(parte.length));
  return [...anteriores, ultimoNome].join(' ');
}

/** Exibe X dentro de colchetes/parênteses, padrão seguranca-export. */
export function marcarCheckbox(valor: boolean, espacos = 1): string {
  if (espacos <= 1) {
    return valor ? 'X' : ' ';
  }

  const nbsp = '\u00A0';
  if (!valor) {
    return nbsp.repeat(espacos);
  }

  const pad = espacos - 1;
  const left = Math.floor(pad / 2);
  const right = pad - left;
  return nbsp.repeat(left) + 'X' + nbsp.repeat(right);
}

/** Marca irregularidade pelo índice (0-based) na lista do response. */
export function irregularidadeMarcada(detalhe: ToiDetalhe | null, indice: number): boolean {
  return detalhe?.irregularidades?.[indice]?.resposta === 'SIM';
}

/** Vazio usa NBSP para não colapsar no HTML e manter o espaçamento dos parênteses. */
export function formatarEconomiaColchete(valor: number): string {
  return valor > 0 ? `${valor}` : ' ';
}

export function somarEconomiaPorCategoria(
  rows: PdfToiEconomiaPorCategoriaRow[] | undefined | null,
  campo: PdfToiEconomiaCategoriaCampo
): number {
  if (!rows?.length) {
    return 0;
  }

  return rows.reduce((total, row) => total + parseEconomiaValor(row[campo]), 0);
}

function parseEconomiaValor(valor: string | number | null | undefined): number {
  if (valor == null || valor === '') {
    return 0;
  }

  const numero = typeof valor === 'number' ? valor : Number(String(valor).trim());
  return Number.isFinite(numero) ? numero : 0;
}

function normalizarTexto(valor: string): string {
  return valor
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .replace(/[:\s]+$/g, '')
    .replace(/\s+/g, ' ');
}

/**
 * Chave tolerante para comparar labels: mantém apenas letras/dígitos, ignorando
 * pontuação e marcadores que variam entre o layout do PDF e o label gravado pelo
 * app (":", ",", "/", "[ * ]", "N°"/"Nº", espaços em torno da barra).
 */
function chaveLabelChecklist(valor: string): string {
  return normalizarTexto(valor).replace(/[^a-z0-9]+/g, '');
}
function valorChecklistLimpo(item: PdfToiChecklistItem | undefined): string {
  return item?.valor ? item.valor.trim().replace(/[\t\n\r]/g, ' ').replace(/\s+/g, ' ') : '';
}

export function checklistValorPorLabel(
  checklist: PdfToiChecklistItem[] | undefined | null,
  label: string
): string {
  if (!checklist?.length) {
    return '';
  }

  const alvo = normalizarTexto(label);
  const alvoChave = chaveLabelChecklist(label);
  const item =
    checklist.find((entry) => normalizarTexto(entry.label ?? '') === alvo) ??
    checklist.find((entry) => chaveLabelChecklist(entry.label ?? '') === alvoChave);
  return valorChecklistLimpo(item);
}
/** Label do checklist do grau de parentesco/afinidade com o usuário. */
export function isLabelParentescoToi(label: string): boolean {
  const normalizado = normalizarTexto(label);
  return normalizado.includes('parentesco') || normalizado.includes('afinidade');
}
/**
 * Valor de "se não for o usuário, informar parentesco/afinidade com o mesmo".
 * O label gravado pelo app varia ("cliente"/"usuário", com ou sem "[ * ]" e
 * espaçamento diferente na barra), então casa por palavra-chave em vez de
 * exigir o texto exato do layout.
 */
export function resolverParentescoChecklistToi(
  checklist: PdfToiChecklistItem[] | undefined | null
): string {
  const item = (checklist ?? []).find((entry) => isLabelParentescoToi(entry.label ?? ''));
  return valorChecklistLimpo(item);
}

/** Mapeia qualquer texto ("INTERNA", "interno", "Localização Externa"...) para "Interna" | "Externa". */
function mapearLocalizacao(valor: string | null | undefined): 'Interna' | 'Externa' | '' {
  const normalizado = normalizarTexto(valor ?? '');
  if (normalizado.includes('intern')) {
    return 'Interna';
  }
  if (normalizado.includes('extern')) {
    return 'Externa';
  }
  return '';
}

/** Campo principal do checklist (label exatamente "LOCALIZAÇÃO"). */
export function isLabelLocalizacaoPrincipalToi(label: string): boolean {
  return normalizarTexto(label) === 'localizacao';
}

/** Opções de seleção (ex.: "Interna"/"Externa") gravadas em itens separados do checklist. */
export function isLabelLocalizacaoOpcaoToi(label: string): boolean {
  const normalizado = normalizarTexto(label);
  if (normalizado === 'localizacao' || normalizado.startsWith('foto') || normalizado.includes('infracao')) {
    return false;
  }
  // Apenas "interna" ou "externa" exatos (não substring matches)
  return normalizado === 'interna' || normalizado === 'externa';
}

export function deveOcultarItemChecklistLocalizacao(item: PdfToiChecklistItem): boolean {
  return isLabelLocalizacaoOpcaoToi(item.label ?? '');
}

/** Localização representada por um item do checklist ("Interna" | "Externa" | ""). */
function localizacaoDoItem(item: PdfToiChecklistItem): 'Interna' | 'Externa' | '' {
  const label = item.label ?? '';

  // Item principal "LOCALIZAÇÃO": o próprio valor é o texto (INTERNA/EXTERNA).
  if (isLabelLocalizacaoPrincipalToi(label)) {
    return mapearLocalizacao(item.valor);
  }

  // Opção marcada (SIM/X): a localização vem do label ("Interna"/"Externa").
  if (isLabelLocalizacaoOpcaoToi(label) && respostaPositiva(item.valor)) {
    return mapearLocalizacao(label);
  }

  return '';
}

export type LocalizacaoToiOpcao = 'Interna' | 'Externa';

/**
 * Opção de localização resolvida —
 * 1) checklist do técnico (item "LOCALIZAÇÃO" ou opção Interna/Externa marcada),
 * 2) fallback: campo automático da API (`localizacaoPadrao`).
 */
export function resolverLocalizacaoTipo(
  checklist: PdfToiChecklistItem[] | undefined | null,
  localizacaoAutomatica?: string | null
): LocalizacaoToiOpcao | '' {
  for (const item of checklist ?? []) {
    const localizacao = localizacaoDoItem(item);
    if (localizacao) {
      return localizacao;
    }
  }

  return mapearLocalizacao(localizacaoAutomatica);
}

/**
 * Texto de "Localização:" para exibição —
 * 1) checklist do técnico (item "LOCALIZAÇÃO" ou opção Interna/Externa marcada),
 * 2) fallback: campo automático da API (`localizacaoPadrao`).
 */
export function resolverLocalizacaoTexto(
  checklist: PdfToiChecklistItem[] | undefined | null,
  localizacaoAutomatica?: string | null
): string {
  const tipo = resolverLocalizacaoTipo(checklist, localizacaoAutomatica);
  if (tipo) {
    return tipo;
  }

  return localizacaoAutomatica?.trim() ?? '';
}

export function marcarLocalizacaoToi(
  opcao: LocalizacaoToiOpcao,
  checklist: PdfToiChecklistItem[] | undefined | null,
  localizacaoAutomatica?: string | null
): boolean {
  return resolverLocalizacaoTipo(checklist, localizacaoAutomatica) === opcao;
}

const DATA_BR_REGEX = /^(\d{2})\/(\d{2})\/(\d{4})/;

/** Label do checklist que representa a data de vistoria. */
export function isLabelDataVistoriaToi(label: string): boolean {
  return normalizarTexto(label).includes('data vistoria');
}

/** Formata valor de data do checklist para exibição (DD/MM/AAAA). */
export function formatarDataChecklistToi(valor: string | null | undefined): string {
  const raw = valor ? valor.trim().replace(/[\t\n\r]/g, ' ').replace(/\s+/g, ' ') : '';
  if (!raw) {
    return '';
  }

  const matchBr = raw.match(DATA_BR_REGEX);
  if (matchBr) {
    return `${matchBr[1]}/${matchBr[2]}/${matchBr[3]}`;
  }

  const matchIsoDate = raw.match(/^(\d{4})-(\d{2})-(\d{2})(?:$|T)/);
  if (matchIsoDate) {
    return `${matchIsoDate[3]}/${matchIsoDate[2]}/${matchIsoDate[1]}`;
  }

  const parsed = new Date(raw);
  if (!Number.isNaN(parsed.getTime())) {
    return formatDate(parsed, 'dd/MM/yyyy', 'pt-BR');
  }

  return raw;
}

export function checklistMarcada(
  checklist: PdfToiChecklistItem[] | undefined | null,
  label: string
): boolean {
  const valor = normalizarTexto(checklistValorPorLabel(checklist, label));
  return valor === 'sim' || valor === 'x' || valor === 'true' || valor === '1';
}

export function irregularidadeDeveMarcar(
  marcar: PdfToiIrregularidadeMarcarItem[] | undefined | null,
  chave: string,
  detalhe?: ToiDetalhe | null
): boolean {
  const alvo = normalizarTexto(chave);
  if ((marcar ?? []).some((item) => normalizarTexto(item.texto) === alvo)) {
    return true;
  }

  return (detalhe?.irregularidades ?? []).some((item) => {
    const chaveItem = item.texto ?? item.chave;
    if (!chaveItem || normalizarTexto(chaveItem) !== alvo) {
      return false;
    }
    return respostaPositiva(item.resposta);
  });
}

const CHAVE_CONSTRUCAO_DISPOSICAO_MATERIAL = '02_construcao_disposicao_material';

const LABELS_OUTROS_CHECKLIST = ['Outros:', 'Preencher em caso de outros:'] as const;

function resolverTextoOutrosChecklist(
  checklist: PdfToiChecklistItem[] | undefined | null
): string {
  for (const label of LABELS_OUTROS_CHECKLIST) {
    const valor = checklistValorPorLabel(checklist, label);
    if (valor) {
      return valor;
    }
  }
  return '';
}

/**
 * Descrição da infração no PDF: quando a irregularidade
 * `02_construcao_disposicao_material` está marcada, acrescenta o texto
 * preenchido no checklist em "Outros:".
 */
export function resolverDescricaoInfracaoToi(
  dsTipoInfracaoBase: string | null | undefined,
  checklist: PdfToiChecklistItem[] | undefined | null,
  irregularidadeMarcar: PdfToiIrregularidadeMarcarItem[] | undefined | null,
  detalhe?: ToiDetalhe | null
): string {
  const base = (dsTipoInfracaoBase ?? '').trim();

  const deveAcrescentarOutros = irregularidadeDeveMarcar(
    irregularidadeMarcar,
    CHAVE_CONSTRUCAO_DISPOSICAO_MATERIAL,
    detalhe
  );
  if (!deveAcrescentarOutros) {
    return base;
  }

  const outros = resolverTextoOutrosChecklist(checklist);
  if (!outros) {
    return base;
  }
  if (!base) {
    return outros;
  }
  if (base.toLowerCase().includes(outros.toLowerCase())) {
    return base;
  }

  return `${base}: ${outros}`;
}

function respostaPositiva(resposta: string | undefined): boolean {
  const valor = normalizarTexto(String(resposta ?? ''));
  return valor === 'sim' || valor === 'x' || valor === 'true' || valor === '1';
}

const FOTOS_TOI_BASE_URL = 'https://copasaimg.blob.core.windows.net/maxline-images';

export function isUrlMidiaToi(valor: string): boolean {
  const trimmed = valor.trim();
  return trimmed.startsWith('http://') || trimmed.startsWith('https://');
}

export function extrairNomeArquivoMidiaToi(valor: string): string {
  const trimmed = valor.trim();
  if (!trimmed) {
    return '';
  }

  if (isUrlMidiaToi(trimmed)) {
    const semQuery = trimmed.split('?')[0];
    return semQuery.split('/').pop() ?? trimmed;
  }

  return trimmed;
}

/** Resolve URL de mídia TOI: aceita link absoluto (SIGOS) ou nome de arquivo legado. */
export function resolverUrlMidiaToi(valor: string): string {
  const trimmed = valor.trim();
  if (!trimmed) {
    return '';
  }

  if (isUrlMidiaToi(trimmed) || trimmed.startsWith('data:')) {
    return trimmed;
  }

  return resolverUrlFotoToi(trimmed);
}

export function resolverUrlFotoToi(nomeArquivo: string): string {
  const ano = nomeArquivo.substring(0, 4);
  const mes = nomeArquivo.substring(4, 6);
  const dia = nomeArquivo.substring(6, 8);
  return `${FOTOS_TOI_BASE_URL}/${ano}/${mes}/${dia}/${nomeArquivo}`;
}

function isArquivoImagemToi(valor: string): boolean {
  const nome = extrairNomeArquivoMidiaToi(valor);
  return /^[0-9]{8}.+\.(jpe?g|png|gif|webp)$/i.test(nome);
}

function isAssinaturaBase64(valor: string): boolean {
  if (valor.startsWith('http://') || valor.startsWith('https://') || valor.startsWith('data:')) {
    return false;
  }

  const trimmed = valor.trim();
  // Assinaturas em base64 são longas; evita tratar CPF, nomes curtos ou "SIM" como imagem.
  if (trimmed.length < 80) {
    return false;
  }

  return /^[A-Za-z0-9+/]*={0,2}$/.test(trimmed);
}

export function resolverChecklistAssinaturaSrc(valor: string | null | undefined): string | null {
  if (!valor?.trim()) {
    return null;
  }

  const trimmed = valor.trim().replace(/[\t\n\r]/g, ' ').replace(/\s+/g, ' ');
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://') || trimmed.startsWith('data:')) {
    return trimmed;
  }
  if (isArquivoImagemToi(trimmed)) {
    return resolverUrlMidiaToi(trimmed);
  }
  if (isAssinaturaBase64(trimmed)) {
    return `data:image/jpeg;base64,${trimmed}`;
  }
  return null;
}

export function temChecklistAssinatura(valor: string | null | undefined): boolean {
  return resolverChecklistAssinaturaSrc(valor) !== null;
}
